import { actions as barcodeActions } from 'Data/ReduxViewModels/Barcode';
import { hasEmptyValue, hasValue } from 'Lib/Any';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

/**
 * 100 ms seems to be sufficient time for the wedge scanner to read a barcode, while accidentally pressing the
 * backquote symbol on the keyboard should only let the user reasonably lose 0-1 characters.
 */
const WEDGE_TIMEOUT_IN_MS = 500;

function permittedCharacters(key: string) {
  return hasValue(key) && key.length === 1 && key.match(/^[A-Za-z0-9-]/);
}

function AddWedgeScannerListener() {
  const dispatch = useDispatch();
  const virtualKeyBarcodeStart = useSelector(
    (state) => state?.user?.data?.HomeLaboratory?.Options?.VirtualKeyBarcodeStartQwa
  );
  const isFrame = useSelector(() => window.qest.isFrame);

  useEffect(() => {
    if (hasEmptyValue(virtualKeyBarcodeStart)) return;
    const barcodeHeaderListener = (event: KeyboardEvent) => {
      //When chrome auto-populates username/password into the login screen, a keyboard event with an undefined key is triggered
      //As such we need handling for when the key is undefined.
      if (hasValue(event.key) && hasValue(virtualKeyBarcodeStart) && event.key === virtualKeyBarcodeStart) {
        dispatch(barcodeActions.barcodeClear());
        dispatch(barcodeActions.setWedgeScanning(true));
        event.preventDefault();
      }
    };

    if (isFrame) window.removeEventListener('keydown', barcodeHeaderListener);
    else window.addEventListener('keydown', barcodeHeaderListener);
  }, [dispatch, isFrame, virtualKeyBarcodeStart]);
}

function BarcodeWedgeScannerListener() {
  const dispatch = useDispatch();
  const location = useLocation();
  const listeningForWedgeScan = useSelector((state) => state.barcode.isListeningForWedgeScan);
  const homeLabUuid = useSelector((state) => state.user.data.HomeLaboratory?.Id);
  const selectedLabUuid = useSelector((state) => state.worksheetGrid.query.params?.LaboratoryId);
  const timer = useRef<NodeJS.Timeout>();
  const isFrame = useSelector(() => window.qest.isFrame);

  const KeyboardWedgeWriting = useCallback(
    (event: KeyboardEvent) => {
      if (!hasValue(event.key)) return;

      if (event.key === 'Enter') {
        dispatch(barcodeActions.setWedgeScanning(false));
        if (location.pathname === `/grid/dry-mass-entry/`) {
          dispatch(barcodeActions.postBarcode(selectedLabUuid));
        } else {
          dispatch(barcodeActions.postBarcode(homeLabUuid));
        }
        event.preventDefault();
        return;
      }

      if (permittedCharacters(event.key)) dispatch(barcodeActions.updateBarcode({ barcode: event.key, concat: true }));
      event.preventDefault();
    },
    [dispatch, homeLabUuid, selectedLabUuid, location.pathname]
  );

  useEffect(() => {
    if (listeningForWedgeScan && !isFrame) {
      window.addEventListener('keydown', KeyboardWedgeWriting);
      dispatch(barcodeActions.barcodeClear());
      timer.current = setTimeout(() => {
        dispatch(barcodeActions.setWedgeScanning(false));
      }, WEDGE_TIMEOUT_IN_MS);
    } else {
      window.removeEventListener('keydown', KeyboardWedgeWriting);
      clearTimeout(timer.current);
    }
  }, [dispatch, KeyboardWedgeWriting, listeningForWedgeScan, isFrame]);
}

export { AddWedgeScannerListener, BarcodeWedgeScannerListener };
